import { browserHistory } from 'react-router'

/**
 * The filters this screen keeps in the URL, so a link carries what the sender
 * was looking at.
 *
 * Codes, not labels: `channel=API` rather than "API Integration", and the
 * client's key rather than its name, because labels and names change while
 * these do not. A filter set to "all" is left out entirely — an absent
 * parameter and "everything" are the same thing here, and a stored `all` would
 * be ambiguous the day a channel is added.
 */
export interface CensusRunUrlState {
  clientPublicKey: string
  channel: string
  firstName: string
  lastName: string
  page: number
  limit: number
  /** The run to open on arrival, so a link can point at one run. */
  runId: string
}

export const DEFAULT_PAGE = 1
export const DEFAULT_LIMIT = 10

/**
 * What react-router puts in a query slot: one value, a repeated one, or
 * nothing. Narrower than `unknown`, and it is the whole set — a reader can see
 * that a repeated parameter is handled rather than wonder what else may arrive.
 */
export type QueryValue = string | string[] | undefined

const readString = (value: QueryValue): string =>
  typeof value === 'string' ? value : ''

const readNumber = (value: QueryValue, fallback: number): number => {
  const parsed = Number(readString(value))
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

export const readCensusRunUrl = (
  query: Record<string, QueryValue> | undefined
): CensusRunUrlState => ({
  clientPublicKey: readString(query?.client),
  channel: readString(query?.channel),
  firstName: readString(query?.firstName),
  lastName: readString(query?.lastName),
  page: readNumber(query?.page, DEFAULT_PAGE),
  limit: readNumber(query?.limit, DEFAULT_LIMIT),
  runId: readString(query?.run),
})

const toQuery = (state: CensusRunUrlState): Record<string, string> => {
  const query: Record<string, string> = {}
  if (state.clientPublicKey) query.client = state.clientPublicKey
  if (state.channel) query.channel = state.channel
  if (state.firstName) query.firstName = state.firstName
  if (state.lastName) query.lastName = state.lastName
  if (state.page !== DEFAULT_PAGE) query.page = String(state.page)
  if (state.limit !== DEFAULT_LIMIT) query.limit = String(state.limit)
  if (state.runId) query.run = state.runId
  return query
}

/**
 * Writes the filters back to the URL.
 *
 * Replaces rather than pushes: the name inputs change as an admin types, and a
 * back button that steps through every keystroke is worse than useless.
 */
export const writeCensusRunUrl = (state: CensusRunUrlState): void => {
  const location = browserHistory.getCurrentLocation()
  const next = toQuery(state)
  const current = (location.query ?? {}) as Record<string, string>
  const isSame =
    Object.keys(next).length === Object.keys(current).length &&
    Object.entries(next).every(([key, value]) => current[key] === value)
  if (isSame) {
    return
  }
  browserHistory.replace({ pathname: location.pathname, query: next })
}
